// Tạo mới / sửa / ban hành lại tài liệu: tóm tắt (có kiểm tra), file đính kèm (IndexedDB), xem trước, phân quyền chia sẻ
import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, DatePicker, Form, Input, Modal, Select, Switch, Table, Tabs, Tooltip, TreeSelect, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Check, Eye, FileX2, Plus, Search, Trash2, UploadCloud, Users, Wand2 } from 'lucide-react';
import DocShell, { EmptyState } from 'pages/Documents/components/DocShell';
import FileViewer from 'pages/Documents/components/FileViewer';
import useDocAuthActions from 'pages/Documents/components/useDocActions';
import useDocsStore, { type DocInput, type SaveAction } from 'pages/Documents/store';
import { ALLOWED_EXT, BLOCKS, DEPARTMENTS, ISSUERS, MAX_TOTAL_SIZE, SIGNERS, deptName, type DocFile, type Employee } from 'pages/Documents/data';
import { buildCategoryTree, descendantIds, fileExt, fmtSize, matches, nextDocCode, recipientIds, validateUploads, type CategoryNode } from 'pages/Documents/logic';
import { putFile, deleteFiles } from 'pages/Documents/fileStore';
import { FileTypeIcon, docLink, useDocAuth, usePeople } from 'pages/Documents/utils';

interface FormValues {
  title: string;
  typeId?: string;
  categoryId?: string;
  blockId?: string;
  number: string;
  code: string;
  effective?: [Dayjs | null, Dayjs | null];
  unitId?: string;
  issuerId?: string;
  signerId?: string;
  summary: string;
  draftMode: boolean;
  internalShare: boolean;
  emailNotify: boolean;
  reissue: boolean;
  replacesId?: string;
}

const uid = () => `f-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
const isEmpty = (v: unknown) => v === undefined || v === null || (typeof v === 'string' && !v.trim());

export default function AddNewDocument() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get('id');
  const reissueId = params.get('reissue');
  const presetCat = params.get('cat');
  const { can, me } = useDocAuth();
  const { perm } = useDocAuthActions();
  const people = usePeople();
  const types = useDocsStore(s => s.types);
  const categories = useDocsStore(s => s.categories);
  const docs = useDocsStore(s => s.docs);
  const saveDoc = useDocsStore(s => s.saveDoc);
  const [form] = Form.useForm<FormValues>();
  const [modal, ctx] = Modal.useModal();

  const editing = editId ? docs.find(d => d.id === editId) : undefined;
  const source = reissueId ? docs.find(d => d.id === reissueId) : undefined;
  const shareOnly = !!editing && editing.status === 'published';
  const creatorId = editing?.createdBy ?? me.id;

  const [tab, setTab] = useState('summary');
  const [files, setFiles] = useState<DocFile[]>([]);
  const [localBlobs, setLocalBlobs] = useState<Record<string, Blob>>({});
  const [removedIds, setRemovedIds] = useState<string[]>([]);
  const [shareAll, setShareAll] = useState(false);
  const [sharedUserIds, setSharedUserIds] = useState<string[]>([]);
  const [summaryErrors, setSummaryErrors] = useState(0);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [viewFileId, setViewFileId] = useState<string>();
  const [dragOver, setDragOver] = useState(false);
  const strict = useRef(false);
  const autoCode = useRef(true);
  const inputRef = useRef<HTMLInputElement>(null);
  const initialized = useRef(false);

  const typeId = Form.useWatch('typeId', form);
  const reissue = Form.useWatch('reissue', form);
  const internalShare = Form.useWatch('internalShare', form);
  const draftMode = Form.useWatch('draftMode', form);
  const summary = Form.useWatch('summary', form) || '';

  // Khởi tạo form theo chế độ: tạo mới / sửa / ban hành lại
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    if (editing) {
      autoCode.current = false;
      form.setFieldsValue({
        title: editing.title, typeId: editing.typeId, categoryId: editing.categoryId, blockId: editing.blockId, number: editing.number, code: editing.code,
        effective: [dayjs(editing.effectiveFrom), editing.effectiveTo ? dayjs(editing.effectiveTo) : null],
        unitId: editing.unitId, issuerId: editing.issuerId, signerId: editing.signerId, summary: editing.summary,
        draftMode: editing.draftMode, internalShare: editing.internalShare, emailNotify: editing.emailNotify,
        reissue: !!editing.replacesId, replacesId: editing.replacesId ?? undefined,
      });
      setFiles(editing.files);
      setShareAll(editing.shareAll);
      setSharedUserIds(editing.sharedUserIds);
    } else {
      const base = source;
      const cat = !base && presetCat ? categories.find(c => c.id === presetCat && c.active) : undefined;
      const type = types.find(t => t.id === (base?.typeId ?? cat?.typeId));
      form.setFieldsValue({
        title: base?.title ?? '', typeId: base?.typeId ?? cat?.typeId, categoryId: base?.categoryId ?? cat?.id, number: '',
        blockId: base?.blockId ?? DEPARTMENTS.find(d => d.id === people[0].deptId)?.blockId,
        code: type ? nextDocCode(docs, type.prefix) : '',
        effective: [dayjs().add(base ? 1 : 0, 'day'), null],
        unitId: base?.unitId ?? people[0].deptId, issuerId: base?.issuerId, signerId: base?.signerId, summary: base?.summary ?? '',
        draftMode: true, internalShare: base?.internalShare ?? true, emailNotify: true,
        reissue: !!base, replacesId: base?.id,
      });
      if (base) { setShareAll(base.shareAll); setSharedUserIds(base.sharedUserIds); }
    }
  }, [editing, source, form, types, docs, people, categories, presetCat]);

  // Cảnh báo khi rời trang chưa lưu
  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  const backUrl = editing ? docLink(editing.id) : can('edit') ? '/document-management' : '/personal-documents';
  const pageTitle = editing ? (shareOnly ? 'Cập nhật phân quyền tài liệu' : 'Sửa tài liệu') : source ? 'Ban hành lại tài liệu' : 'Tạo mới tài liệu';

  // ---- Danh sách chọn
  const inactiveIds = useMemo(() => {
    const set = new Set<string>();
    categories.filter(c => !c.active).forEach(c => { set.add(c.id); descendantIds(categories, c.id).forEach(id => set.add(id)); });
    return set;
  }, [categories]);
  const categoryTree = useMemo(() => {
    const toNode = (n: CategoryNode): any => ({
      value: n.id, title: n.name,
      disabled: inactiveIds.has(n.id) || !types.find(t => t.id === n.typeId)?.active,
      children: n.children?.map(toNode),
    });
    return buildCategoryTree(categories, c => !typeId || c.typeId === typeId).map(toNode);
  }, [categories, typeId, inactiveIds, types]);
  const reissueOptions = docs
    .filter(d => !d.deletedAt && d.status === 'published' && !d.replacedById && d.id !== editing?.id)
    .map(d => ({ value: d.id, label: `${d.code} · ${d.title}` }));

  const onTypeChange = (id: string) => {
    const t = types.find(x => x.id === id);
    const cat = categories.find(c => c.id === form.getFieldValue('categoryId'));
    if (cat && cat.typeId !== id) form.setFieldValue('categoryId', undefined);
    if (t && (autoCode.current || !form.getFieldValue('code'))) {
      form.setFieldValue('code', nextDocCode(docs, t.prefix));
      autoCode.current = true;
    }
  };
  const onCategoryChange = (id?: string) => {
    const cat = categories.find(c => c.id === id);
    if (cat && cat.typeId !== form.getFieldValue('typeId')) { form.setFieldValue('typeId', cat.typeId); onTypeChange(cat.typeId); }
  };
  const onUnitChange = (id: string) => {
    if (!form.getFieldValue('blockId')) form.setFieldValue('blockId', DEPARTMENTS.find(d => d.id === id)?.blockId);
  };
  const genCode = () => {
    const t = types.find(x => x.id === form.getFieldValue('typeId'));
    if (!t) return message.warning('Chọn loại tài liệu trước để sinh mã');
    form.setFieldValue('code', nextDocCode(docs, t.prefix));
    autoCode.current = true;
    form.validateFields(['code']);
  };
  const onReissuePick = (id?: string) => {
    const src = docs.find(d => d.id === id);
    if (src && !form.getFieldValue('typeId')) {
      form.setFieldsValue({ typeId: src.typeId, categoryId: src.categoryId });
      onTypeChange(src.typeId);
    }
  };

  // ---- File đính kèm
  const addFiles = (list: FileList | File[]) => {
    const incoming = Array.from(list);
    const { accepted, errors } = validateUploads(files, incoming);
    errors.forEach(e => message.error(e, 4));
    if (!accepted.length) return;
    const added: DocFile[] = [];
    const blobs: Record<string, Blob> = {};
    accepted.forEach(i => {
      const f = incoming[i];
      const id = uid();
      added.push({ id, name: f.name, size: f.size, ext: fileExt(f.name), uploadedAt: new Date().toISOString(), uploadedBy: me.name });
      blobs[id] = f;
    });
    setFiles(prev => [...prev, ...added]);
    setLocalBlobs(prev => ({ ...prev, ...blobs }));
    setDirty(true);
    message.success(`Đã thêm ${added.length} file`);
  };
  const removeFile = (f: DocFile) => {
    setFiles(prev => prev.filter(x => x.id !== f.id));
    if (localBlobs[f.id]) setLocalBlobs(({ [f.id]: _drop, ...rest }) => rest);
    else if (!f.demo) setRemovedIds(prev => [...prev, f.id]);
    setDirty(true);
  };
  const totalSize = files.reduce((s, f) => s + f.size, 0);

  // ---- Phân quyền
  const recipients = recipientIds({ shareAll, internalShare: !!internalShare, sharedUserIds, createdBy: creatorId }, people);
  const creatorDept = people.find(p => p.id === creatorId)?.deptId;

  // ---- Lưu
  const save = async (action: SaveAction) => {
    strict.current = action === 'submit' || action === 'publish';
    let v: FormValues;
    try {
      v = await form.validateFields();
      setSummaryErrors(0);
    } catch (e: any) {
      setSummaryErrors(e?.errorFields?.length || 1);
      setTab('summary');
      message.error('Vui lòng kiểm tra lại các trường bắt buộc');
      return;
    }
    if (strict.current && !files.length) {
      setTab('summary');
      message.error('Cần ít nhất 1 file đính kèm trước khi gửi duyệt / ban hành');
      return;
    }
    const proceed = async () => {
      setSaving(true);
      try {
        await Promise.all(Object.entries(localBlobs).filter(([id]) => files.some(f => f.id === id)).map(([id, blob]) => putFile(id, blob)));
      } catch {
        setSaving(false);
        message.error('Không lưu được file vào bộ nhớ trình duyệt (có thể đã hết dung lượng).');
        return;
      }
      const input: DocInput = {
        title: v.title.trim(), typeId: v.typeId!, categoryId: v.categoryId!, blockId: v.blockId || '', number: (v.number || '').trim(), code: v.code.trim(),
        effectiveFrom: (v.effective?.[0] || dayjs()).format('YYYY-MM-DD'), effectiveTo: v.effective?.[1] ? v.effective[1].format('YYYY-MM-DD') : null,
        unitId: v.unitId || '', issuerId: v.issuerId || '', signerId: v.signerId || '', summary: (v.summary || '').trim(),
        draftMode: !!v.draftMode, internalShare: !!v.internalShare, emailNotify: !!v.emailNotify,
        replacesId: v.reissue ? v.replacesId ?? null : null, shareAll, sharedUserIds, files,
      };
      const doc = saveDoc(input, { id: editing?.id, action, by: me });
      deleteFiles(removedIds);
      setDirty(false);
      setSaving(false);
      message.success(
        action === 'share' ? 'Đã cập nhật phân quyền' : action === 'draft' ? 'Đã lưu dự thảo' : action === 'submit' ? 'Đã gửi duyệt' : `Đã ban hành ${doc.code}`,
      );
      navigate(docLink(doc.id));
    };
    if (action !== 'draft' && recipients.length === 0) {
      modal.confirm({
        title: 'Tài liệu chưa chia sẻ cho ai',
        content: 'Chỉ bạn và người quản lý tài liệu xem được. Vẫn tiếp tục?',
        okText: 'Tiếp tục', cancelText: 'Chọn người chia sẻ',
        onOk: proceed, onCancel: () => setTab('share'),
      });
    } else proceed();
  };

  const cancel = () => {
    if (!dirty) return navigate(backUrl);
    modal.confirm({
      title: 'Hủy các thay đổi chưa lưu?', content: 'Dữ liệu bạn vừa nhập sẽ bị mất.', okText: 'Hủy thay đổi', okButtonProps: { danger: true }, cancelText: 'Tiếp tục sửa',
      onOk: () => { setDirty(false); navigate(backUrl); },
    });
  };

  // ---- Không có quyền / không tìm thấy
  if (editId && !editing) {
    return <DocShell title="Sửa tài liệu"><EmptyState icon={<FileX2 />} title="Không tìm thấy tài liệu" desc="Tài liệu không tồn tại hoặc đã bị xóa vĩnh viễn." action={<Button onClick={() => navigate('/document-management')}>Về danh sách</Button>} /></DocShell>;
  }
  if (editing && (!perm.edit(editing) || editing.deletedAt)) {
    return <DocShell title="Sửa tài liệu"><EmptyState icon={<FileX2 />} title="Không thể sửa tài liệu này" desc="Bạn không có quyền sửa, hoặc tài liệu đã thu hồi / bị thay thế / nằm trong thùng rác." action={<Button onClick={() => navigate(docLink(editing.id))}>Xem tài liệu</Button>} /></DocShell>;
  }
  if (reissueId && (!source || !perm.reissue(source))) {
    return <DocShell title="Ban hành lại"><EmptyState icon={<FileX2 />} title="Không thể ban hành lại" desc="Chỉ ban hành lại được tài liệu đang ban hành và chưa bị thay thế." action={<Button onClick={() => navigate(-1)}>Quay lại</Button>} /></DocShell>;
  }

  const strictRule = (msg: string) => ({ validator: (_r: unknown, v: unknown) => (strict.current && isEmpty(v) ? Promise.reject(new Error(msg)) : Promise.resolve()) });
  const disabled = shareOnly;

  // ---- Bảng phân quyền
  const selectableIds = new Set(people.filter(p => p.id !== creatorId).map(p => p.id));
  const toggleUser = (id: string, on: boolean) => {
    setSharedUserIds(prev => (on ? Array.from(new Set([...prev, id])) : prev.filter(x => x !== id)));
    setDirty(true);
  };

  const summaryTab = (
    <Form<FormValues>
      form={form} layout="horizontal" labelCol={{ flex: '150px' }} labelAlign="left" labelWrap colon={false} disabled={disabled}
      onValuesChange={() => setDirty(true)} className="[&_.ant-form-item]:mb-4"
    >
      {shareOnly && <Alert className="!mb-4" type="info" showIcon message="Tài liệu đã ban hành chỉ được chỉnh sửa phân quyền chia sẻ. Muốn thay đổi nội dung, hãy dùng “Ban hành lại” ở màn chi tiết." />}
      {editing?.status === 'rejected' && editing.rejectReason && <Alert className="!mb-4" type="error" showIcon message="Tài liệu bị từ chối" description={editing.rejectReason} />}
      {editing?.status === 'pending' && <Alert className="!mb-4" type="warning" showIcon message="Tài liệu đang chờ duyệt. Lưu dự thảo sẽ rút tài liệu khỏi danh sách chờ duyệt." />}
      <div className="grid grid-cols-1 gap-x-10 lg:grid-cols-2">
        <div>
          <Form.Item name="title" label="Tiêu đề" required rules={[{ required: true, whitespace: true, message: 'Nhập tiêu đề' }, { max: 255, message: 'Tối đa 255 ký tự' }]}>
            <Input placeholder="Nhập tiêu đề" maxLength={255} />
          </Form.Item>
          <Form.Item name="typeId" label="Loại tài liệu" required rules={[{ required: true, message: 'Chọn loại tài liệu' }]}>
            <Select placeholder="Chọn loại tài liệu" onChange={onTypeChange} showSearch optionFilterProp="label"
              options={types.filter(t => t.active || t.id === typeId).map(t => ({ value: t.id, label: t.name, disabled: !t.active }))} />
          </Form.Item>
          <Form.Item name="categoryId" label="Danh mục" required rules={[{ required: true, message: 'Chọn danh mục' }]}>
            <TreeSelect placeholder="Chọn danh mục tài liệu" treeData={categoryTree} treeDefaultExpandAll showSearch treeNodeFilterProp="title" onChange={onCategoryChange}
              notFoundContent={typeId ? 'Loại này chưa có danh mục đang sử dụng' : 'Không có danh mục'} />
          </Form.Item>
          <Form.Item name="blockId" label="Khối/nghiệp vụ" required rules={[strictRule('Chọn khối/nghiệp vụ')]}>
            <Select placeholder="Chọn khối/nghiệp vụ" allowClear options={BLOCKS.map(b => ({ value: b.id, label: b.name }))} />
          </Form.Item>
          <Form.Item name="number" label="Số tài liệu" rules={[{ max: 50, message: 'Tối đa 50 ký tự' }]}>
            <Input placeholder="VD: 125/2026" maxLength={50} />
          </Form.Item>
          <Form.Item
            name="code" label="Mã tài liệu" required
            rules={[
              { required: true, whitespace: true, message: 'Nhập mã tài liệu' },
              { max: 60, message: 'Tối đa 60 ký tự' },
              { validator: (_r, v) => (v && docs.some(d => d.id !== editing?.id && d.code.trim().toUpperCase() === String(v).trim().toUpperCase()) ? Promise.reject(new Error('Mã tài liệu đã tồn tại (kể cả trong thùng rác)')) : Promise.resolve()) },
            ]}
          >
            <Input placeholder="VD: 001/2026/QĐ-KHOTHI" maxLength={60} onChange={() => { autoCode.current = false; }}
              suffix={!disabled && <Tooltip title="Sinh mã tự động theo loại"><Wand2 size={15} className="cursor-pointer text-[#357f96]" onClick={genCode} /></Tooltip>} />
          </Form.Item>
        </div>
        <div>
          <Form.Item
            name="effective" label="Ngày hiệu lực" required
            rules={[
              strictRule('Chọn ngày bắt đầu hiệu lực'),
              {
                validator: (_r, v?: [Dayjs | null, Dayjs | null]) => {
                  if (strict.current && !v?.[0]) return Promise.reject(new Error('Chọn ngày bắt đầu hiệu lực'));
                  const src = docs.find(d => d.id === form.getFieldValue('replacesId'));
                  if (form.getFieldValue('reissue') && src && v?.[0] && v[0].format('YYYY-MM-DD') <= src.effectiveFrom) {
                    return Promise.reject(new Error(`Phải sau ngày hiệu lực của bản cũ (${dayjs(src.effectiveFrom).format('DD/MM/YYYY')})`));
                  }
                  return Promise.resolve();
                },
              },
            ]}
            extra="Để trống ngày kết thúc nếu không thời hạn"
          >
            <DatePicker.RangePicker className="w-full" format="DD/MM/YYYY" allowEmpty={[false, true]} placeholder={['Từ ngày', 'Đến ngày (không bắt buộc)']} />
          </Form.Item>
          <Form.Item name="unitId" label="Đơn vị ban hành" required rules={[strictRule('Chọn đơn vị ban hành')]}>
            <Select placeholder="Chọn đơn vị ban hành" allowClear onChange={onUnitChange} options={DEPARTMENTS.map(d => ({ value: d.id, label: d.name }))} />
          </Form.Item>
          <Form.Item name="issuerId" label="Chủ thể ban hành" required rules={[strictRule('Chọn chủ thể ban hành')]}>
            <Select placeholder="Chọn chủ thể ban hành" allowClear options={ISSUERS.map(i => ({ value: i.id, label: i.name }))} />
          </Form.Item>
          <Form.Item name="signerId" label="Người ký" required rules={[strictRule('Chọn người ký')]}>
            <Select placeholder="Chọn người ký" allowClear showSearch optionFilterProp="label"
              options={SIGNERS.map(e => ({ value: e.id, label: `${e.name} — ${e.title}` }))} />
          </Form.Item>
          <Form.Item label="Ban hành lại" className="!mb-0">
            <div className="flex items-start gap-3">
              <Form.Item name="reissue" valuePropName="checked" noStyle>
                <Switch disabled={disabled || !!source} onChange={on => { if (!on) form.setFieldValue('replacesId', undefined); }} />
              </Form.Item>
              <Form.Item
                name="replacesId" className="flex-1"
                rules={[{ validator: (_r, v) => (form.getFieldValue('reissue') && !v ? Promise.reject(new Error('Chọn tài liệu được thay thế')) : Promise.resolve()) }]}
              >
                <Select disabled={disabled || !reissue || !!source} placeholder="Chọn tài liệu được thay thế" allowClear showSearch optionFilterProp="label"
                  options={reissueOptions} onChange={onReissuePick} />
              </Form.Item>
            </div>
          </Form.Item>
        </div>
      </div>

      <Form.Item name="summary" label="Mô tả ngắn" rules={[{ max: 1000, message: 'Tối đa 1000 ký tự' }]} extra={<span className={summary.length > 1000 ? 'text-red-500' : ''}>{summary.length}/1000</span>}>
        <Input.TextArea rows={4} placeholder="Nhập mô tả ngắn về văn bản" maxLength={1000} />
      </Form.Item>

      <div className="space-y-3 pl-0 lg:pl-[150px]">
        {[
          { name: 'draftMode', label: 'Mở góp ý dự thảo', desc: 'Khi lưu dự thảo, người được chia sẻ có thể xem và góp ý trước khi ban hành.' },
          { name: 'internalShare', label: 'Chia sẻ nội bộ', desc: `Chia sẻ cho toàn bộ ${deptName(creatorDept || '')} (phòng ban của người tạo).` },
          { name: 'emailNotify', label: 'Gửi email thông báo', desc: 'Gửi email đến người được chia sẻ khi tài liệu được ban hành.' },
        ].map(o => (
          <div key={o.name} className="flex items-start gap-3">
            <Form.Item name={o.name} valuePropName="checked" noStyle><Switch size="small" disabled={o.name === 'draftMode' ? disabled : false} /></Form.Item>
            <div className="-mt-0.5 text-[13px]"><span className="font-medium text-slate-700">{o.label}</span><span className="ml-2 text-slate-500">{o.desc}</span></div>
          </div>
        ))}
      </div>

      <div className="mt-6 border-t border-slate-200 pt-5">
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <h3 className="text-[14px] font-bold text-slate-800">Tài liệu đính kèm <span className="text-red-500">*</span></h3>
          {!disabled && <Button size="small" shape="round" icon={<Plus size={14} />} onClick={() => inputRef.current?.click()}>Thêm</Button>}
          <span className="text-xs text-slate-500">Định dạng: {ALLOWED_EXT.map(e => e.toUpperCase()).join(', ')} · Tổng tối đa {fmtSize(MAX_TOTAL_SIZE)} · Đã dùng {fmtSize(totalSize)}</span>
          <input ref={inputRef} type="file" multiple hidden accept={ALLOWED_EXT.map(e => `.${e}`).join(',')}
            onChange={e => { if (e.target.files) addFiles(e.target.files); e.target.value = ''; }} />
        </div>
        {!disabled && (
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
            onClick={() => inputRef.current?.click()}
            className={`mb-3 flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed py-4 text-sm transition-colors ${dragOver ? 'border-[#3e91ab] bg-[#ecf6f9] text-[#2c6b7f]' : 'border-slate-200 text-slate-400 hover:border-[#3e91ab]/60'}`}
          >
            <UploadCloud size={18} /> Kéo thả file vào đây hoặc bấm để chọn
          </div>
        )}
        <Table<DocFile>
          size="small" rowKey="id" dataSource={files} pagination={false} scroll={{ x: 520 }}
          locale={{ emptyText: 'Chưa có file đính kèm' }}
          columns={[
            {
              title: 'File', key: 'name',
              render: (_v, f) => (
                <button type="button" onClick={() => { setViewFileId(f.id); setTab('view'); }} className="flex items-center gap-2 text-left text-[#357f96] hover:underline">
                  <FileTypeIcon ext={f.ext} /> <span className="truncate">{f.name}</span>
                  {localBlobs[f.id] && <span className="rounded bg-amber-50 px-1.5 text-[11px] text-amber-700">Chưa lưu</span>}
                </button>
              ),
            },
            { title: 'Kích thước', dataIndex: 'size', width: 110, align: 'right', sorter: (a, b) => a.size - b.size, render: v => fmtSize(v) },
            { title: 'Loại', dataIndex: 'ext', width: 80, align: 'center', render: v => String(v).toUpperCase() },
            {
              title: 'Hành động', key: 'act', width: 100, align: 'center',
              render: (_v, f) => (
                <div className="flex items-center justify-center gap-3">
                  <Tooltip title="Xem"><button type="button" onClick={() => { setViewFileId(f.id); setTab('view'); }} className="text-slate-400 hover:text-[#357f96]"><Eye size={16} /></button></Tooltip>
                  {!disabled && <Tooltip title="Gỡ file"><button type="button" onClick={() => removeFile(f)} className="text-red-400 hover:text-red-600"><Trash2 size={16} /></button></Tooltip>}
                </div>
              ),
            },
          ]}
        />
      </div>
    </Form>
  );

  const shareTab = <ShareTable
    people={people.filter(p => selectableIds.has(p.id))} shareAll={shareAll} sharedUserIds={sharedUserIds} creatorDept={creatorDept}
    internalShare={!!internalShare} recipients={recipients.length}
    onShareAll={on => { setShareAll(on); setDirty(true); }} onToggle={toggleUser}
    onBulk={(ids, on) => { setSharedUserIds(prev => (on ? Array.from(new Set([...prev, ...ids])) : prev.filter(x => !ids.includes(x)))); setDirty(true); }}
  />;

  const canPublish = can('approve');
  const t = types.find(x => x.id === typeId);

  return (
    <DocShell
      title={pageTitle}
      subtitle={editing ? `${editing.code}` : source ? `Thay thế ${source.code}` : undefined}
      breadcrumb={
        <div className="mb-3 flex items-center gap-2 text-sm">
          <Link to={backUrl}><LeftOutlined className="flex h-6 w-6 items-center justify-center rounded-full bg-[#3e91ab] text-white hover:bg-[#357f96]" /></Link>
          <Link to={can('edit') ? '/document-management' : '/personal-documents'} className="text-000000e0">{can('edit') ? 'Quản lý tài liệu' : 'Tài liệu cá nhân'}</Link>
          <RightOutlined className="text-[12px] text-gray-500" />
          <span className="text-000000e0">{pageTitle}</span>
        </div>
      }
    >
      {ctx}
      <div className="rounded-lg border border-slate-200 bg-white">
        <Tabs
          activeKey={tab} onChange={setTab} className="px-4 [&_.ant-tabs-nav]:!mb-0"
          items={[
            { key: 'summary', label: <Badge count={summaryErrors} size="small" offset={[8, -2]}>Tóm tắt tài liệu</Badge> },
            { key: 'view', label: `Xem tài liệu (${files.length})` },
            { key: 'share', label: <span className="inline-flex items-center gap-1.5">Phân quyền <span className="rounded-full bg-[#ecf6f9] px-1.5 text-xs text-[#2c6b7f]">{recipients.length}</span></span> },
          ]}
        />
        <div className="border-t border-slate-200 p-4">
          <div className={tab === 'summary' ? '' : 'hidden'}>{summaryTab}</div>
          {tab === 'view' && (
            <FileViewer files={files} localBlobs={localBlobs} fileId={viewFileId} onFileChange={setViewFileId}
              placeholder={{ title: form.getFieldValue('title') || 'Chưa có tiêu đề', code: form.getFieldValue('code') || '', summary: summary, unit: deptName(form.getFieldValue('unitId') || '') }} />
          )}
          {tab === 'share' && shareTab}
        </div>
        <div className="sticky bottom-0 z-10 flex flex-wrap items-center gap-3 border-t border-slate-200 bg-white px-4 py-3">
          <Button danger onClick={cancel} className="min-w-[96px]">Hủy</Button>
          {shareOnly ? (
            <Button type="primary" loading={saving} onClick={() => save('share')} className="min-w-[120px]">Lưu phân quyền</Button>
          ) : (
            <>
              <Tooltip title={draftMode ? 'Người được chia sẻ có thể xem & góp ý' : 'Chỉ bạn và người quản lý xem được'}>
                <Button loading={saving} onClick={() => save('draft')} className="min-w-[120px]">Lưu dự thảo</Button>
              </Tooltip>
              {canPublish ? (
                <Button type="primary" loading={saving} icon={<Check size={15} />} onClick={() => save('publish')}>Lưu & ban hành</Button>
              ) : (
                <Button type="primary" loading={saving} onClick={() => save('submit')}>Gửi duyệt</Button>
              )}
            </>
          )}
          <span className="ml-auto hidden text-xs text-slate-400 md:inline">
            {t ? <>Mã gợi ý theo ký hiệu <b>{t.prefix}</b> · </> : null}Trường có dấu * bắt buộc khi gửi duyệt / ban hành
          </span>
        </div>
      </div>
    </DocShell>
  );
}

// ---------------------------------------------------------------------------

interface ShareProps {
  people: Employee[];
  shareAll: boolean;
  sharedUserIds: string[];
  internalShare: boolean;
  creatorDept?: string;
  recipients: number;
  onShareAll: (on: boolean) => void;
  onToggle: (id: string, on: boolean) => void;
  onBulk: (ids: string[], on: boolean) => void;
}

function ShareTable({ people, shareAll, sharedUserIds, internalShare, creatorDept, recipients, onShareAll, onToggle, onBulk }: ShareProps) {
  const [q, setQ] = useState('');
  const [block, setBlock] = useState('');
  const [dept, setDept] = useState('');
  const [onlySelected, setOnlySelected] = useState(false);
  const viaInternal = (p: Employee) => internalShare && p.deptId === creatorDept;
  const isOn = (p: Employee) => shareAll || viaInternal(p) || sharedUserIds.includes(p.id);
  const rows = people.filter(p =>
    matches(q, p.name, p.username, deptName(p.deptId), p.title) &&
    (!block || DEPARTMENTS.find(d => d.id === p.deptId)?.blockId === block) &&
    (!dept || p.deptId === dept) &&
    (!onlySelected || isOn(p)));
  const rowIds = rows.filter(p => !viaInternal(p)).map(p => p.id);
  const allRowsOn = rowIds.length > 0 && rowIds.every(id => sharedUserIds.includes(id));

  const columns: ColumnsType<Employee> = [
    { title: 'STT', key: 'stt', width: 60, align: 'center', render: (_v, _r, i) => i + 1 },
    { title: 'Vùng miền', dataIndex: 'region', width: 110, sorter: (a, b) => a.region.localeCompare(b.region) },
    { title: 'Khối/nghiệp vụ', key: 'block', width: 190, render: (_v, p) => BLOCKS.find(b => b.id === DEPARTMENTS.find(d => d.id === p.deptId)?.blockId)?.name },
    { title: 'Đơn vị', dataIndex: 'hq', width: 170 },
    { title: 'Phòng ban', key: 'dept', width: 160, render: (_v, p) => deptName(p.deptId), sorter: (a, b) => deptName(a.deptId).localeCompare(deptName(b.deptId)) },
    { title: 'Tài khoản', dataIndex: 'username', width: 200, render: v => <span className="text-slate-500">{v}</span> },
    { title: 'Họ và tên', dataIndex: 'name', width: 170, render: v => <span className="font-medium text-slate-700">{v}</span>, sorter: (a, b) => a.name.localeCompare(b.name) },
    { title: 'Chức vụ', dataIndex: 'title', width: 130 },
    {
      title: 'Chia sẻ', key: 'share', width: 100, align: 'center', fixed: 'right',
      render: (_v, p) => (
        <Tooltip title={shareAll ? 'Đang chia sẻ toàn công ty' : viaInternal(p) ? 'Được chia sẻ qua “Chia sẻ nội bộ”' : undefined}>
          <Switch size="small" checked={isOn(p)} disabled={shareAll || viaInternal(p)} onChange={on => onToggle(p.id, on)} />
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input allowClear prefix={<Search size={15} className="text-slate-400" />} placeholder="Tìm nhân viên, tài khoản, phòng ban…" value={q} onChange={e => setQ(e.target.value)} className="w-full sm:w-72" />
        <Select value={block} onChange={v => { setBlock(v); setDept(''); }} className="w-48" options={[{ value: '', label: 'Tất cả khối' }, ...BLOCKS.map(b => ({ value: b.id, label: b.name }))]} />
        <Select value={dept} onChange={setDept} className="w-44" options={[{ value: '', label: 'Tất cả phòng ban' }, ...DEPARTMENTS.filter(d => !block || d.blockId === block).map(d => ({ value: d.id, label: d.name }))]} />
        <label className="flex items-center gap-2 text-sm text-slate-600"><Switch size="small" checked={onlySelected} onChange={setOnlySelected} /> Chỉ người đã chọn</label>
      </div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <button
          type="button" onClick={() => onShareAll(!shareAll)}
          className={`flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-semibold transition-colors ${shareAll ? 'border-[#3e91ab] bg-[#ecf6f9] text-[#2c6b7f]' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'}`}
        >
          <span className={`flex h-4 w-4 items-center justify-center rounded border ${shareAll ? 'border-[#357f96] bg-[#357f96]' : 'border-slate-300'}`}>{shareAll && <Check size={12} className="text-white" strokeWidth={3} />}</span>
          Chọn tất cả (Toàn công ty)
        </button>
        {!shareAll && rowIds.length > 0 && (
          <Button size="small" onClick={() => onBulk(rowIds, !allRowsOn)}>{allRowsOn ? `Bỏ chọn ${rowIds.length} người đang lọc` : `Chọn ${rowIds.length} người đang lọc`}</Button>
        )}
        <div className="ml-auto flex items-center gap-2 rounded border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-600">
          <Users size={16} className="text-[#3e91ab]" /> Đã chia sẻ: <b className="text-[#2c6b7f]">{recipients}</b> nhân sự
          {internalShare && !shareAll && <span className="text-xs text-slate-400">(gồm chia sẻ nội bộ {deptName(creatorDept || '')})</span>}
        </div>
      </div>
      <Table<Employee>
        size="small" rowKey="id" columns={columns} dataSource={rows} scroll={{ x: 1300 }}
        rowClassName={p => (isOn(p) ? 'bg-[#ecf6f9]/50' : '')}
        pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: [10, 20, 50], showTotal: (t, r) => `${r[0]}-${r[1]} trong ${t} nhân sự` }}
        locale={{ emptyText: 'Không có nhân sự phù hợp' }}
      />
    </div>
  );
}
